import { useMemo } from 'react';
import type { LLTaskDefinition, LLTaskElement, LLTaskSubmission } from '../../types';
import { TextElement } from './TextElement';
import { HeaderElement } from './HeaderElement';
import { ImageElement } from './ImageElement';
import { VideoElement } from './VideoElement';
import { LinkElement } from './LinkElement';
import { CheckboxElement } from './CheckboxElement';
import { MultipleChoiceElement } from './MultipleChoiceElement';
import { TextInputElement } from './TextInputElement';
import { CheckInventoryElement } from './CheckInventoryElement';
import { CheckGameStateElement } from './CheckGameStateElement';

interface Props {
  definition: LLTaskDefinition;
  value: LLTaskSubmission;
  onChange: (next: LLTaskSubmission) => void;
  /** When true, every element renders read-only (after-submit view). */
  disabled?: boolean;
}

/**
 * Renders every element in `definition.elements` in `index` order, dispatching
 * to the typed component for each `kind`. The submission state is owned by the
 * parent — this component is fully controlled.
 *
 * `check_inventories` and `check_game_states` are display-only here; their
 * server-side gates are evaluated when the player submits. The companion
 * `useDefinitionGate` hook in `./useDefinitionGate.ts` reports whether
 * the local-state gates are satisfied so the parent can disable Submit.
 */
export function DefinitionRenderer({ definition, value, onChange, disabled }: Props) {
  const sorted = useMemo(
    () => [...definition.elements].sort((a, b) => a.index - b.index),
    [definition.elements],
  );

  const updateCheckbox = (index: number, checked: boolean) => {
    const next = new Set(value.checkboxes);
    if (checked) next.add(index);
    else next.delete(index);
    onChange({ ...value, checkboxes: Array.from(next).sort((a, b) => a - b) });
  };

  const updateMultipleChoice = (index: number, optionId: string) => {
    const others = value.multipleChoices.filter((mc) => mc.index !== index);
    onChange({
      ...value,
      multipleChoices: [...others, { index, optionId }].sort((a, b) => a.index - b.index),
    });
  };

  const updateTextInput = (responseTag: string, newValue: string) => {
    const others = value.textInputs.filter((ti) => ti.responseTag !== responseTag);
    onChange({ ...value, textInputs: [...others, { responseTag, value: newValue }] });
  };

  return (
    <div className="space-y-4">
      {sorted.map((el) => (
        <ElementRow
          key={`${el.kind}-${el.index}`}
          element={el}
          value={value}
          disabled={disabled}
          onCheckboxChange={updateCheckbox}
          onMcChange={updateMultipleChoice}
          onTextInputChange={updateTextInput}
        />
      ))}
    </div>
  );
}

interface ElementRowProps {
  element: LLTaskElement;
  value: LLTaskSubmission;
  disabled?: boolean;
  onCheckboxChange: (index: number, checked: boolean) => void;
  onMcChange: (index: number, optionId: string) => void;
  onTextInputChange: (responseTag: string, value: string) => void;
}

function ElementRow({
  element,
  value,
  disabled,
  onCheckboxChange,
  onMcChange,
  onTextInputChange,
}: ElementRowProps) {
  switch (element.kind) {
    case 'text':
      return <TextElement element={element} />;
    case 'header':
      return <HeaderElement element={element} />;
    case 'image':
      return <ImageElement element={element} />;
    case 'video':
      return <VideoElement element={element} />;
    case 'link':
      return <LinkElement element={element} />;
    case 'checkbox':
      return (
        <CheckboxElement
          element={element}
          checked={value.checkboxes.includes(element.index)}
          onChange={(checked) => onCheckboxChange(element.index, checked)}
          disabled={disabled}
        />
      );
    case 'multipleChoice':
      return (
        <MultipleChoiceElement
          element={element}
          selectedOptionId={
            value.multipleChoices.find((mc) => mc.index === element.index)?.optionId ?? null
          }
          onChange={(optionId) => onMcChange(element.index, optionId)}
          disabled={disabled}
        />
      );
    case 'textInput':
      return (
        <TextInputElement
          element={element}
          value={value.textInputs.find((ti) => ti.responseTag === element.responseTag)?.value ?? ''}
          onChange={(next) => onTextInputChange(element.responseTag, next)}
          disabled={disabled}
        />
      );
    case 'checkInventory':
      return <CheckInventoryElement element={element} />;
    case 'checkGameState':
      return <CheckGameStateElement element={element} />;
    default: {
      const _exhaustive: never = element;
      void _exhaustive;
      return null;
    }
  }
}
